// §7.4 — "Client keeps `syncState: path → { rev, sha, dirty }` in OPFS."
//
// Two adapters, both thin on purpose:
//
//   • `createVfsSyncFiles` puts the sync client on the **real** tree, through the `/System`-capable
//     internal view. Sync writes are not app writes: they arrive from the server and must land
//     wherever the server says, without going through §9.5's per-app confinement, which exists to
//     bound what an *app* may reach.
//   • `createOpfsSyncState` persists the bookkeeping. It is kept in memory and flushed, rather than
//     written per entry, because a sync run touches every path it knows about and a write per
//     entry would be thousands of OPFS round trips for one run.
//
// The state file lives under `/System` (§7.1 keeps that out of every app's reach) so no app can
// read what the user has synced or, worse, mark something clean that is not.
import type { SyncEntry, VfsPath } from '@hangar/shared';
import type { VfsInternal } from './tree';
import type { SyncFileStore, SyncStateStore } from './sync-client';

/** §7.1 — `/System` is unreachable from `HangarAPI`, which is exactly what this needs. */
export const SYNC_STATE_PATH = '/System/.sync.json';

interface PersistedSyncState {
  readonly v: 1;
  readonly cursor: number;
  readonly entries: Record<string, SyncEntry>;
}

export function createVfsSyncFiles(files: VfsInternal): SyncFileStore {
  return {
    read: (path) => files.read(path),
    write: async (path, bytes) => {
      await files.write(path, bytes);
    },
    remove: async (path) => {
      await files.remove(path);
    },
    exists: (path) => files.exists(path),
    // D-194 — the receiving half of a folder. `internal.mkdir` creates any missing ancestors
    // too, so a nested folder arriving before its parent still lands correctly.
    mkdir: async (path) => {
      await files.mkdir(path);
    },
    // §34.6 / D-496 — `stat` rather than `read().length`: the threshold exists precisely for files
    // nobody wants in memory, so asking how big one is must not load it.
    size: async (path) => (await files.stat(path))?.size ?? 0,
  };
}

export interface OpfsSyncState extends SyncStateStore {
  /** Read what was persisted. Call once, before the first run. */
  hydrate(): Promise<void>;
  /** Write it back. Cheap enough to call after every run, and only then. */
  flush(): Promise<void>;
}

export function createOpfsSyncState(files: VfsInternal): OpfsSyncState {
  const entries = new Map<VfsPath, SyncEntry>();
  let cursor = 0;

  return {
    get: (path) => entries.get(path),
    set: (path, entry) => void entries.set(path, entry),
    delete: (path) => void entries.delete(path),
    paths: () => [...entries.keys()],
    cursor: () => cursor,
    setCursor: (value) => {
      // Monotonic (D-157): a page answering with an older cursor must never rewind past one we
      // have already applied, or the next run replays changes that are already on disk.
      cursor = Math.max(cursor, value);
    },

    async hydrate() {
      try {
        const raw = await files.read(SYNC_STATE_PATH);
        const parsed = JSON.parse(new TextDecoder().decode(raw)) as PersistedSyncState;
        if (parsed.v !== 1) return;
        cursor = typeof parsed.cursor === 'number' ? parsed.cursor : 0;
        for (const [path, entry] of Object.entries(parsed.entries ?? {})) {
          entries.set(path, entry);
        }
      } catch {
        // No file, or one that will not parse. Starting from cursor 0 is always **safe**: the
        // server replays from the beginning and every change is applied over content that is
        // already identical, which content addressing makes free (D-047).
      }
    },

    async flush() {
      const payload: PersistedSyncState = {
        v: 1,
        cursor,
        entries: Object.fromEntries(entries),
      };
      await files.write(SYNC_STATE_PATH, new TextEncoder().encode(JSON.stringify(payload)));
    },
  };
}
